"use client"

import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"

export function ThemeToggle({ className }: { className?: string }) {
  const [theme, setTheme] = useState<"light" | "dark" | null>(null)

  useEffect(() => {
    // Determine the initial theme on the client
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
    const initialTheme = savedTheme || (prefersDark ? "dark" : "light")
    setTheme(initialTheme)
  }, [])

  const toggleTheme = () => {
    if (!theme) return
    const newTheme = theme === "light" ? "dark" : "light"
    setTheme(newTheme)
    
    // Set the class on HTML element
    if (newTheme === "dark") {
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
    
    localStorage.setItem("theme", newTheme)
  }

  // To prevent hydration mismatch, render a placeholder of the same size
  if (theme === null) {
    return <div className="w-10 h-10 shrink-0" />
  }

  return (
    <button
      onClick={toggleTheme}
      className={cn(
        "relative flex items-center justify-center w-10 h-10 rounded-sm bg-transparent hover:bg-action-secondary hover:text-action active:bg-action-secondary-pressed transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-action shrink-0",
        className
      )}
      aria-label="Toggle color mode"
      data-testid="theme-toggle-button"
    >
      <div className="relative w-5 h-5 flex items-center justify-center">
        {/* Sun Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(
            "w-5 h-5 absolute transition-all duration-500 ease-out transform",
            theme === "dark"
              ? "rotate-90 scale-0 opacity-0 text-secondary"
              : "rotate-0 scale-100 opacity-100 text-secondary"
          )}
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </svg>

        {/* Moon Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(
            "w-5 h-5 absolute transition-all duration-500 ease-out transform",
            theme === "dark"
              ? "rotate-0 scale-100 opacity-100 text-warning"
              : "-rotate-90 scale-0 opacity-0 text-secondary"
          )}
        >
          <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
        </svg>
      </div>
    </button>
  )
}
